import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, ShieldQuestion } from 'lucide-react';
import { api } from '../../../api/client';
import InfoTip from '../../../components/InfoTip';
import { toast } from '../../../lib/toast';

export default function VerificationSection({ domain }) {
  const qc = useQueryClient();

  const verifyQuery = useQuery({
    queryKey: ['verify-status', domain._id],
    queryFn: () =>
      api.get(`/api/domains/${domain._id}/verification-status`).then((r) => r.data),
  });

  const verifyMutation = useMutation({
    mutationFn: () => api.post(`/api/domains/${domain._id}/verify`).then((r) => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['verify-status', domain._id] });
      if (data?.verified) toast.success('Domain verified');
    },
    onError: (err) => toast.error(err, 'Verification failed'),
  });

  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <ShieldCheck className="w-4 h-4" />
          </span>
          Domain verification
          <InfoTip
            title="Why verify"
            body="Proves you own this domain before we let you publish llms.txt under your site, generate white-label reports, or expose your data via the public API. One-time check; the file can stay or be removed after."
          />
        </div>
        {verifyQuery.data?.verified ? (
          <span className="pill-success">
            <ShieldCheck className="w-3 h-3" /> Verified
          </span>
        ) : (
          <span className="pill-warn">
            <ShieldQuestion className="w-3 h-3" /> Not verified
          </span>
        )}
      </div>
      <p className="text-sm text-gray-600 mb-3">
        Create a small text file and upload it to your site, then click verify. Once
        verified you can publish <code>llms.txt</code> and generate shareable reports.
      </p>
      {verifyQuery.data && !verifyQuery.data.verified && (
        <>
          {(() => {
            const path = verifyQuery.data.instructions?.httpFile?.path || '';
            const fileName = path.split('/').pop();
            const folder = path.slice(0, path.length - fileName.length);
            const fullUrl = `https://${domain.domain}${path}`;
            return (
              <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 mb-3 text-xs space-y-3">
                <div>
                  <div className="font-semibold text-ink-700 mb-0.5">File name:</div>
                  <code className="break-all select-all">{fileName}</code>
                  <div className="text-gray-500 mt-1">
                    The token below, with a <code>.txt</code> extension.
                  </div>
                </div>
                <div>
                  <div className="font-semibold text-ink-700 mb-0.5">
                    Where to place it:
                  </div>
                  <code className="break-all select-all">{folder}</code>
                  <div className="text-gray-500 mt-1">
                    A folder at the root of your website. Create the{' '}
                    <code>.well-known</code> and <code>geo-verify</code> folders if they
                    don&apos;t exist.
                  </div>
                </div>
                <div>
                  <div className="font-semibold text-ink-700 mb-0.5">
                    Final URL (must return HTTP 200):
                  </div>
                  <code className="break-all select-all">{fullUrl}</code>
                </div>
                <div>
                  <div className="font-semibold text-ink-700 mb-0.5">File contents:</div>
                  <code className="break-all select-all">{verifyQuery.data.token}</code>
                  <div className="text-gray-500 mt-1">
                    Plain text only — just the token, nothing else.
                  </div>
                </div>
              </div>
            );
          })()}
          <button
            className="btn-primary"
            onClick={() => verifyMutation.mutate()}
            disabled={verifyMutation.isPending}
          >
            <ShieldCheck className="w-4 h-4" />
            {verifyMutation.isPending ? 'Checking…' : 'Verify ownership'}
          </button>
        </>
      )}
    </div>
  );
}
